import React from 'react';
import { X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { StreamingCodeBlock } from '../../../../components/common/StreamingCodeBlock';

interface LearningObjectivesModalProps {
  isOpen: boolean;
  onClose: () => void;
  learningObjectives?: string;
  lessonLabel?: string;
}

interface FieldDef {
  key: string;
  label: string;
  emoji: string;
  cardBg: string;
  border: string;
  iconBg: string;
  textColor: string;
  shadowColor: string;
}

const FIELD_DEFS: FieldDef[] = [
  { key: '核心能力模型', label: '核心能力模型', emoji: '🎯', cardBg: 'from-pink-50 to-pink-100', border: 'border-pink-200', iconBg: 'from-pink-300 to-rose-300', textColor: 'text-pink-600', shadowColor: '255,182,217' },
  { key: '知识点拆解', label: '知识点拆解', emoji: '🧩', cardBg: 'from-violet-50 to-violet-100', border: 'border-violet-200', iconBg: 'from-violet-300 to-purple-300', textColor: 'text-violet-600', shadowColor: '200,182,226' },
  { key: 'AI实战项目', label: 'AI实战项目', emoji: '🤖', cardBg: 'from-emerald-50 to-emerald-100', border: 'border-emerald-200', iconBg: 'from-emerald-300 to-teal-300', textColor: 'text-emerald-600', shadowColor: '181,234,215' },
  { key: '解决痛点', label: '解决痛点', emoji: '💡', cardBg: 'from-orange-50 to-orange-100', border: 'border-orange-200', iconBg: 'from-orange-300 to-amber-300', textColor: 'text-orange-600', shadowColor: '255,216,181' },
  { key: '学完马上能做什么', label: '学完马上能做什么', emoji: '🚀', cardBg: 'from-sky-50 to-sky-100', border: 'border-sky-200', iconBg: 'from-sky-300 to-blue-300', textColor: 'text-sky-600', shadowColor: '181,216,247' },
  { key: '未来能做的大事', label: '未来能做的大事', emoji: '🌟', cardBg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-200', iconBg: 'from-yellow-300 to-amber-300', textColor: 'text-yellow-700', shadowColor: '255,244,181' },
  { key: '知识进阶路线', label: '知识进阶路线', emoji: '📈', cardBg: 'from-purple-50 to-purple-100', border: 'border-purple-200', iconBg: 'from-purple-300 to-fuchsia-300', textColor: 'text-purple-600', shadowColor: '212,197,249' },
];

type MarkdownPart =
  | { type: 'text'; content: string }
  | { type: 'code'; content: string; language: string };

// 将 markdown 文本拆分为「文本段」与「代码段」，复用 ChatMessage.tsx 的成熟正则
const splitMarkdown = (content: string): MarkdownPart[] => {
  const parts: MarkdownPart[] = [];
  if (!content) return parts;
  const regex = /```(\w+)?\s*([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.substring(lastIndex, match.index) });
    }
    const lang = match[1] || 'text';
    const code = match[2].trim();
    parts.push({ type: 'code', content: code, language: lang });
    lastIndex = regex.lastIndex;
  }
  const remaining = content.substring(lastIndex);
  if (remaining) parts.push({ type: 'text', content: remaining });
  return parts;
};

// 渲染 AI实战项目 字段的 markdown 内容：文本段用 ReactMarkdown + prose，代码段用 StreamingCodeBlock
const renderMarkdownField = (content: string) => {
  const parts = splitMarkdown(content);
  return (
    <div className="pl-14 space-y-2">
      {parts.map((part, i) => {
        if (part.type === 'code') {
          return (
            <StreamingCodeBlock
              key={i}
              code={part.content}
              language={part.language}
              isTruncated={false}
              theme="light"
            />
          );
        }
        const trimmed = part.content.trim();
        if (!trimmed) return null;
        return (
          <div
            key={i}
            className="prose prose-sm max-w-none
              prose-headings:text-emerald-800
              prose-h1:text-lg prose-h1:mt-3 prose-h1:mb-1
              prose-h2:text-base prose-h2:mt-3 prose-h2:mb-1 prose-h2:border-b prose-h2:border-emerald-200 prose-h2:pb-1
              prose-h3:text-sm prose-h3:mt-2 prose-h3:mb-1
              prose-p:text-gray-700 prose-p:my-1.5 prose-p:leading-relaxed
              prose-ul:my-1.5 prose-ul:list-disc prose-ul:pl-5 prose-ul:text-gray-700
              prose-ol:my-1.5 prose-ol:list-decimal prose-ol:pl-5 prose-ol:text-gray-700
              prose-li:my-0.5
              prose-strong:text-emerald-900 prose-strong:font-semibold
              prose-code:text-pink-700 prose-code:bg-pink-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
              prose-blockquote:border-l-emerald-400 prose-blockquote:bg-emerald-50/50 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:rounded-r prose-blockquote:not-italic
              prose-a:text-emerald-600 prose-a:underline"
          >
            <ReactMarkdown>{trimmed}</ReactMarkdown>
          </div>
        );
      })}
    </div>
  );
};

const LearningObjectivesModal: React.FC<LearningObjectivesModalProps> = ({ isOpen, onClose, learningObjectives, lessonLabel }) => {
  if (!isOpen) return null;

  let parsed: Record<string, string> | null = null;
  let parseError = false;
  if (learningObjectives) {
    try {
      const obj = JSON.parse(learningObjectives);
      if (typeof obj === 'object' && obj !== null) {
        parsed = obj as Record<string, string>;
      } else {
        parseError = true;
      }
    } catch {
      parseError = true;
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-[640px] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-[#FFFBF5] rounded-3xl shadow-2xl border border-white/60"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header：糖果色渐变标题栏 */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-gradient-to-r from-pink-100 via-sky-100 to-violet-100 border-b border-white/60">
          <h2 className="text-lg font-bold text-gray-700 flex items-center gap-2">
            <span className="text-2xl">🌈</span>
            学习目标
            {lessonLabel && <span className="text-xs font-normal text-gray-500 ml-2">{lessonLabel}</span>}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/60 text-gray-500 hover:text-gray-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 内容区 */}
        <div className="p-6">
          {!learningObjectives && (
            <div className="py-16 text-center text-gray-400">本课暂未配置学习目标</div>
          )}

          {learningObjectives && parseError && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-md">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-300 to-orange-300 flex items-center justify-center text-white shadow-md border border-white/60">
                  <span className="text-xl">📋</span>
                </div>
                <span className="font-bold text-sm text-amber-700">课程信息</span>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm whitespace-pre-wrap">{learningObjectives}</p>
            </div>
          )}

          {parsed && (
            <div className="grid grid-cols-1 gap-4">
              {FIELD_DEFS.map((field) => {
                const value = parsed![field.key];
                if (!value) return null;
                return (
                  <div
                    key={field.key}
                    className={`bg-gradient-to-br ${field.cardBg} ${field.border} border rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1`}
                    style={{ boxShadow: `0 8px 24px rgba(${field.shadowColor},0.18), 0 2px 6px rgba(${field.shadowColor},0.10)` }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${field.iconBg} flex items-center justify-center shadow-md border-2 border-white/70`}>
                        <span className="text-xl">{field.emoji}</span>
                      </div>
                      <span className={`font-bold text-sm ${field.textColor}`}>{field.label}</span>
                    </div>
                    {field.key === 'AI实战项目'
                      ? renderMarkdownField(value)
                      : <p className="text-gray-700 text-sm leading-relaxed pl-14 whitespace-pre-wrap">{value}</p>
                    }
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningObjectivesModal;
